import React from 'react';
import PropTypes from 'prop-types';
import MessageInput from 'Components/UI/ActivityFeed/Shared/HouseholdFeed/MessageInput';
import Paper from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Paper';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import Typography from 'Components/UI/Typography';
import fr from 'facet-rest';
import useUserContext from 'Context/UserContext';
import { TemplatesProvider } from 'Context/TemplateContext';
import { addQuery, removeQueryWithReplace } from 'Helpers/Query';
import { noop } from 'Helpers/PropTypes';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'unset',
  },
  title: {
    marginBottom: theme.spacing(2),
    fontSize: '16px',
  },
}));

const BulkMessageInput = ({
  className,
  onChange,
  value,
  test,
  resource,
  onSubmit,
  setSubject,
  setTemplateId,
  ...rest
}) => {
  const { agentResource } = useUserContext();
  const classes = useStyles();

  const handleSubmit = async () => {
    try {
      resource.attr('sendAt', '');
      resource.attr('isDraft', 'false');
      await resource.submit();
      if (onSubmit) {
        onSubmit();
      }
      displaySuccess('Sent Bulk Communication');
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const handlePreview = async () => {
    removeQueryWithReplace('bulkCommunicationPreviewLink');
    try {
      const [savedResource] = await resource.submit();
      addQuery({ bulkCommunicationLink: savedResource.link('self').url() }, { bulkCommunicationPreviewLink: savedResource.relationship(fr.relationships.preview.name).link('related').url() });
    } catch (err) {
      displayErrorMessage(err, err.errors);
    }
  };

  const addedRecipients = resource.attr('addedRecipients') || [];
  const numOfSelectedRecipients = (resource.attr('filteredRecipients')?.resources().list().filter(el => el.attr('isIncluded') === 'true').length || 0) + (addedRecipients.length || 0);
  const confirmationAlertMessage = `Attention: You are about to send this message to ${numOfSelectedRecipients} household${numOfSelectedRecipients > 1 ? 's' : ''}`;

  return (
    <TemplatesProvider templatesLinkUrl={agentResource?.relationship(fr.relationships.messageTemplates.name).link('related').url()}>
      <Paper variant="tight" className={classNames(classes.root, className)}>
        <Typography
          className={classes.title}
          color={'primary'}
          variant={'h6'}
        >
        Bulk Communication
        </Typography>
        <MessageInput
          setSubject={setSubject}
          setTemplateId={setTemplateId}
          showAttachmentsButton={false}
          setText={onChange}
          text={value}
          test={'bulk-communication-input'}
          onSubmit={handleSubmit}
          needsConfirmOnSubmit={numOfSelectedRecipients != null}
          confirmationTitle={null}
          confirmationText="Do you still want to send this message?"
          confirmationButtonLabel="Yes"
          cancelButtonLabel="No"
          showTemplateOptionsMenu
          TemplateOptionsMenuProps={{
            ButtonProps: {
              test: 'bulk-message-options-button',
            },
          }}
          SubmitButtonProps={{
            test: 'bulk-communication-submit',
            color: 'secondary',
          }}
          showAdditionalButton
          AdditionalButtonProps={{
            test: 'bulk-communication-save-and-preview',
            color: 'primary',
            text: 'Save & Preview',
            successText: 'Saved',
            savingText: 'Saving',
            disabled: numOfSelectedRecipients === 0,
            onClick: handlePreview,
          }}
          confirmationAlertMessage={confirmationAlertMessage}
          disableSubmit={numOfSelectedRecipients === 0}
          MessageInputActionsProps={{
            hideSend: true, // TODO: Clean Up Messages Beta Feature Toggle on promotion
          }}
          {...rest}
        />
      </Paper>
    </TemplatesProvider>
  );
};

BulkMessageInput.defaultProps = {
  className: '',
  test: null,
  onSubmit: null,
  setSubject: noop,
  setTemplateId: noop,
};

BulkMessageInput.propTypes = {
  className: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  test: PropTypes.string,
  onSubmit: PropTypes.func,
  setSubject: PropTypes.func,
  setTemplateId: PropTypes.func,
};

export default BulkMessageInput;
